import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCart } from "../../context/CartContext";
import { useAuth } from "../../context/AuthContext";
import { useBranding } from "../../context/BrandingContext";
import { api, money, imgUrl } from "../../lib/api";
import { openRazorpayCheckout } from "../../lib/razorpay";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../../components/ui/dialog";

export default function Checkout() {
  const { cart, refresh, coupon, setCoupon } = useCart();
  const { user } = useAuth();
  const { branding } = useBranding();
  const navigate = useNavigate();
  const [addr, setAddr] = useState({
    name: user?.name || "", phone: user?.phone || "",
    line1: "", line2: "", city: "", state: "", pincode: "", country: "India",
  });
  const [order, setOrder] = useState(null);
  const [payModal, setPayModal] = useState(false);
  const [intent, setIntent] = useState(null);  // { razorpay_key_id, razorpay_order_id, amount, currency, configured }
  const [processing, setProcessing] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState("online"); // "online" | "cod"

  const subtotal = (cart.items || []).reduce((s, i) => s + (i.product?.price || 0) * i.qty, 0);
  const discount = coupon?.discount || 0;
  const shipping = (subtotal - discount) > 1999 ? 0 : 99;
  const total = Math.max(0, subtotal - discount + shipping);

  // Group cart items by seller for a preview split in the order summary
  const bySeller = (cart.items || []).reduce((acc, it) => {
    const sid = it.product?.seller_id || "unknown";
    if (!acc[sid]) {
      acc[sid] = {
        seller_id: sid,
        shop_name: it.product?.seller?.shop_name || "Seller",
        items: [],
        subtotal: 0,
      };
    }
    acc[sid].items.push(it);
    acc[sid].subtotal += (it.product?.price || 0) * it.qty;
    return acc;
  }, {});
  const splits = Object.values(bySeller);

  const field = (k) => ({
    value: addr[k],
    onChange: (e) => setAddr({ ...addr, [k]: e.target.value }),
    className: "kb-input",
    "data-testid": `addr-${k}`,
  });

  if (!user) return <div className="px-6 py-24 text-center">Sign in to checkout.</div>;
  if (!cart.items?.length) return <div className="px-6 py-24 text-center">Your cart is empty.</div>;

  const placeOrder = async () => {
    const trimmed = Object.fromEntries(Object.entries(addr).map(([k, v]) => [k, (v || "").toString().trim()]));
    const required = ["name", "phone", "line1", "city", "state", "pincode"];
    for (const k of required) {
      if (!trimmed[k]) { toast.error(`Please enter ${k}`); return; }
    }
    if (!/^\d{10}$/.test(trimmed.phone)) { toast.error("Phone must be 10 digits"); return; }
    if (!/^\d{6}$/.test(trimmed.pincode)) { toast.error("Pincode must be 6 digits"); return; }
    if (trimmed.name.length < 2) { toast.error("Please enter a valid name"); return; }

    setProcessing(true);
    try {
      const payload = { address: trimmed, payment_method: paymentMethod };
      if (coupon?.code) payload.coupon_code = coupon.code;
      const { data } = await api.post("/orders/checkout", payload);
      if (paymentMethod === "cod") {
        setCoupon(null);
        await refresh();
        toast.success("Order placed — pay when you receive it!");
        navigate(`/order/${data.order.id}/success`);
        return;
      }
      // Real Razorpay flow (tenant's keys). If `configured` is false the
      // tenant hasn't set up Razorpay yet — show the legacy mock modal so
      // demo storefronts still work, otherwise launch the real Checkout.
      setOrder(data.order);
      setIntent({
        razorpay_key_id: data.razorpay_key_id,
        razorpay_order_id: data.razorpay_order_id,
        amount: data.amount,
        currency: data.currency || "INR",
        configured: data.configured,
      });
      if (!data.configured) {
        // Tenant has no Razorpay → keep the mock modal (existing flow)
        setPayModal(true);
        return;
      }
      // Live: open Razorpay Checkout directly, no app-modal needed.
      try {
        const resp = await openRazorpayCheckout({
          keyId: data.razorpay_key_id,
          orderId: data.razorpay_order_id,
          amount: data.amount,
          currency: data.currency || "INR",
          name: branding?.brand_name || "Store",
          description: `Order ${data.order.order_number || data.order.id.slice(0, 8)}`,
          image: branding?.logo_square_url ? imgUrl(branding.logo_square_url) : undefined,
          prefill: {
            name: trimmed.name,
            contact: trimmed.phone,
            email: user?.email || undefined,
          },
          notes: { order_id: data.order.id },
          themeColor: branding?.theme_color || branding?.primary_color || "#7A1C29",
        });
        await api.post("/orders/confirm", {
          order_id: data.order.id,
          razorpay_order_id: resp.razorpay_order_id,
          razorpay_payment_id: resp.razorpay_payment_id,
          razorpay_signature: resp.razorpay_signature,
        });
        setCoupon(null);
        await refresh();
        toast.success("Payment successful!");
        navigate(`/order/${data.order.id}/success`);
      } catch (rzpErr) {
        if (rzpErr?.message === "dismissed") {
          toast.error("Payment cancelled — your order is held until paid");
        } else {
          toast.error(rzpErr?.message || "Payment failed");
        }
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Checkout failed");
    } finally {
      setProcessing(false);
    }
  };

  const simulatePay = async (success) => {
    if (!success) {
      setPayModal(false);
      toast.error("Payment cancelled");
      return;
    }
    setProcessing(true);
    try {
      const fakePaymentId = `pay_mock_${Math.random().toString(36).slice(2, 14)}`;
      // Mock flow (tenant hasn't configured Razorpay yet) — backend
      // accepts a no-signature confirm for `order_mock_*` order ids.
      await api.post("/orders/confirm", { order_id: order.id, razorpay_payment_id: fakePaymentId });
      setCoupon(null);
      await refresh();
      setPayModal(false);
      toast.success("Payment successful!");
      navigate(`/order/${order.id}/success`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Payment verification failed");
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="px-6 md:px-12 lg:px-24 py-10" data-testid="checkout-page">
      <div className="overline mb-2">Checkout</div>
      <h1 className="font-display text-4xl lg:text-5xl mb-10">One final step.</h1>

      <div className="grid lg:grid-cols-[1fr_380px] gap-12">
        <div className="space-y-10">
          <section>
            <div className="overline mb-4">Delivery Address</div>
            <div className="grid grid-cols-2 gap-4">
              <input placeholder="Full Name" {...field("name")} />
              <input placeholder="Phone" {...field("phone")} />
              <input placeholder="Address Line 1" className="kb-input col-span-2" value={addr.line1} onChange={(e) => setAddr({ ...addr, line1: e.target.value })} data-testid="addr-line1" />
              <input placeholder="Address Line 2 (optional)" className="kb-input col-span-2" value={addr.line2} onChange={(e) => setAddr({ ...addr, line2: e.target.value })} data-testid="addr-line2" />
              <input placeholder="City" {...field("city")} />
              <input placeholder="State" {...field("state")} />
              <input placeholder="Pincode" {...field("pincode")} />
              <input placeholder="Country" {...field("country")} />
            </div>
          </section>

          <section>
            <div className="overline mb-4">Payment</div>
            <div className="space-y-3">
              <label
                className={`flex items-center gap-3 border p-4 cursor-pointer transition-colors ${paymentMethod === "online" ? "border-[hsl(var(--kb-primary))] bg-[hsl(var(--kb-surface-alt))]/40" : "border-[hsl(var(--kb-border))]"}`}
                data-testid="pay-method-online"
              >
                <input
                  type="radio"
                  name="payment_method"
                  checked={paymentMethod === "online"}
                  onChange={() => setPaymentMethod("online")}
                  className="shrink-0"
                />
                <div className="w-10 h-10 bg-[hsl(var(--kb-primary))] text-white flex items-center justify-center font-display text-xl shrink-0">R</div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium">Razorpay · UPI / Cards / Netbanking</div>
                  <div className="text-xs text-[hsl(var(--kb-muted))]">Secure online payment (demo — mocked popup).</div>
                </div>
              </label>
              <label
                className={`flex items-center gap-3 border p-4 cursor-pointer transition-colors ${paymentMethod === "cod" ? "border-[hsl(var(--kb-primary))] bg-[hsl(var(--kb-surface-alt))]/40" : "border-[hsl(var(--kb-border))]"}`}
                data-testid="pay-method-cod"
              >
                <input
                  type="radio"
                  name="payment_method"
                  checked={paymentMethod === "cod"}
                  onChange={() => setPaymentMethod("cod")}
                  className="shrink-0"
                />
                <div className="w-10 h-10 border border-[hsl(var(--kb-primary))] text-[hsl(var(--kb-primary))] flex items-center justify-center font-display text-sm shrink-0">₹</div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium">Cash on Delivery</div>
                  <div className="text-xs text-[hsl(var(--kb-muted))]">Pay when the package arrives at your door.</div>
                </div>
              </label>
            </div>
          </section>

          <button className="kb-btn-primary" onClick={placeOrder} disabled={processing} data-testid="place-order-btn">
            {processing
              ? "Placing order…"
              : paymentMethod === "cod"
                ? `Place Order · ${money(total)}`
                : `Pay ${money(total)}`}
          </button>
        </div>

        <aside className="bg-white border border-[hsl(var(--kb-border))] p-6 h-fit">
          <div className="overline mb-4">Your Order · {splits.length} seller{splits.length === 1 ? "" : "s"}</div>
          <div className="space-y-4 max-h-80 overflow-y-auto" data-testid="order-summary-splits">
            {splits.map((s) => (
              <div key={s.seller_id} className="border-b border-[hsl(var(--kb-border))] pb-3 last:border-b-0" data-testid={`seller-split-${s.seller_id}`}>
                <div className="text-xs uppercase tracking-widest text-[hsl(var(--kb-muted))] mb-2 truncate">
                  {s.shop_name}
                </div>
                {s.items.map((i, idx) => (
                  <div key={idx} className="flex justify-between text-sm py-1">
                    <span className="truncate mr-2">{i.product?.title} × {i.qty}</span>
                    <span>{money((i.product?.price || 0) * i.qty)}</span>
                  </div>
                ))}
                <div className="flex justify-between text-xs text-[hsl(var(--kb-primary))] mt-1">
                  <span>Seller subtotal</span><span>{money(s.subtotal)}</span>
                </div>
              </div>
            ))}
          </div>
          <div className="border-t border-[hsl(var(--kb-border))] mt-4 pt-4 space-y-2 text-sm">
            <div className="flex justify-between"><span>Subtotal</span><span>{money(subtotal)}</span></div>
            {discount > 0 && (
              <div className="flex justify-between text-green-700">
                <span>Coupon ({coupon.code})</span><span data-testid="checkout-discount">−{money(discount)}</span>
              </div>
            )}
            <div className="flex justify-between"><span>Shipping</span><span>{shipping === 0 ? "Free" : money(shipping)}</span></div>
            <div className="flex justify-between font-display text-lg pt-2 border-t border-[hsl(var(--kb-border))]">
              <span>Total</span><span data-testid="checkout-total">{money(total)}</span>
            </div>
          </div>
        </aside>
      </div>

      <Dialog open={payModal} onOpenChange={setPayModal}>
        <DialogContent className="max-w-md rounded-none" data-testid="razorpay-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Razorpay Secure Checkout</DialogTitle>
            <DialogDescription className="text-xs text-[hsl(var(--kb-muted))]">Mocked Razorpay payment popup</DialogDescription>
          </DialogHeader>
          <div className="bg-[hsl(var(--kb-surface-alt))] p-4 text-sm space-y-1">
            <div className="flex justify-between"><span className="text-[hsl(var(--kb-muted))]">Merchant Key:</span><span className="font-mono text-xs">{intent?.razorpay_key_id?.slice(0, 18) || "(mock)"}…</span></div>
            <div className="flex justify-between"><span className="text-[hsl(var(--kb-muted))]">Order ID:</span><span className="font-mono text-xs">{order?.razorpay_order_id?.slice(0, 20)}…</span></div>
            <div className="flex justify-between text-base pt-2"><span>Amount:</span><b>{money(order?.total || 0)}</b></div>
          </div>
          <p className="text-xs text-[hsl(var(--kb-muted))]">This is a simulated Razorpay payment. In production, the real Razorpay checkout will open here using the seller&apos;s key.</p>
          <div className="flex gap-3 mt-4">
            <button
              className="kb-btn-outline flex-1"
              onClick={() => simulatePay(false)}
              disabled={processing}
              data-testid="pay-cancel-btn"
            >
              Cancel
            </button>
            <button
              className="kb-btn-primary flex-1"
              onClick={() => simulatePay(true)}
              disabled={processing}
              data-testid="pay-success-btn"
            >
              {processing ? "Processing…" : "Pay Now"}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
